import { P } from "@cd2/text"
import { assert } from "@cd2/tools"
import React from "react"
import { useEvents } from "../../Components/EventsList"
import { useConfirmCancelConfirmation } from "../../hooks/useConfirmCancelConfirmation"
import { useSiteAlerts } from "../../hooks/useSiteAlerts"
import {
  IItem,
  IQuoteOrderRecord,
  isDeleted,
  itemTypes,
} from "../../stories/quoteOrders/quoteOrderUtils"
import { buildSetNewItemId, handleSaveBuildSet } from "./BuildSetsForm"
import { calculateBuildQuantity } from "../../utils/utils"

export function useSaveBuild(quote?: IQuoteOrderRecord) {
  const skeem = window.skeem
  const events = useEvents()
  const alerts = useSiteAlerts()
  const [confirmSaveModal, handleConfirmSave] = useConfirmCancelConfirmation({
    heading: `Save this BOM for ${quote?.customer?.name}?`,
    body: (
      <React.Fragment>
        <P>Are you sure you want to save this BOM for this customer?</P>
        <P>BOMs saved to a customer are only available to this customer.</P>
        <P>The current item price, child items and their quantities will be saved.</P>
      </React.Fragment>
    ),
  })

  async function handleSaveBuildForCustomer(buildUid: string) {
    if (!quote) {
      return
    }
    if (await handleConfirmSave()) {
      try {
        const buildItems = quote?.items.filter(
          item => !isDeleted(item) && item.build_set_uid == buildUid,
        ) as IItem[]
        if (!buildItems || buildItems.length === 0) {
          throw new Error("No build items found")
        }
        const parentItem = buildItems.find(item => item.type === itemTypes.buildSet)
        const childItems = buildItems.filter(item => item.type === itemTypes.buildSetItem)

        const productId = parentItem?.product?.id
        const customerId = quote?.customer?.id

        assert(productId, "Product is required")
        assert(customerId, "Customer is required")

        await handleSaveBuildSet({
          productId,
          customerId,
          price_pennies: parentItem.price_pennies ?? null,
          build_set_items: childItems.map((item, index) => ({
            id: buildSetNewItemId(),
            optional: !!item.is_rich_quote_optional,
            quantity: calculateBuildQuantity(item.quantity, parentItem.quantity),
            weight: index,
            product: { id: item.product?.id ?? "" },
          })),
        })
        alerts.genericSuccess("BOM saved successfully")
      } catch (e) {
        alerts.genericError("Failed to save BOM")
        console.error(e)
      }
    }
  }

  return {
    handleSaveBuildForCustomer,
    modals: <React.Fragment key="build-set-save-modals">{confirmSaveModal}</React.Fragment>,
  }
}
